import { useTranslation } from "react-i18next"
import { Skeleton, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import type { DrilldownCreativeDrawerModel } from "../../lib/drilldown-drawer-model"

interface DrilldownCreativeMetricsGridProps {
  model: DrilldownCreativeDrawerModel
  rank: number | null
  isRankLoading?: boolean
}

interface MetricCellProps {
  label: string
  value: string
  statusDot?: "active" | "inactive" | null
  isLoading?: boolean
}

function MetricCell({ label, value, statusDot, isLoading }: MetricCellProps) {
  const { t } = useTranslation()

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <p className="text-[10px] font-normal uppercase tracking-[0.06em] text-muted-foreground">{label}</p>
      <div className="flex items-center gap-1.5">
        {isLoading ? (
          <Skeleton className="h-[19px] w-10 rounded" aria-hidden="true" />
        ) : (
          <p className="text-[13px] text-foreground">{value}</p>
        )}
        {statusDot === "active" ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="size-1.5 shrink-0 cursor-default rounded-full bg-emerald-500" aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent side="top">{t("drilldown.drawer.statusActive", "Active")}</TooltipContent>
          </Tooltip>
        ) : null}
        {statusDot === "inactive" ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="size-1.5 shrink-0 cursor-default rounded-full bg-red-500" aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent side="top">{t("drilldown.drawer.statusInactive", "Inactive")}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

export function DrilldownCreativeMetricsGrid({
  model,
  rank,
  isRankLoading = false,
}: DrilldownCreativeMetricsGridProps) {
  const { t } = useTranslation()
  const emptyValue = t("drilldown.drawer.emptyValue", "—")

  const formatLabel =
    model.format === "carousel"
      ? t("common.mediaType.carousel", "Carousel")
      : model.format === "video"
        ? t("common.mediaType.video", "Video")
        : t("common.mediaType.image", "Image")

  return (
    <div className="rounded-[14px] border border-border bg-muted/50 p-4">
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-4">
        <MetricCell
          label={t("drilldown.drawer.launchDate", "Launch Date")}
          value={model.launchDate}
          statusDot={model.launchDateStatus}
        />
        <MetricCell
          label={t("drilldown.drawer.creativeLifespan", "Creative Lifespan")}
          value={t("drilldown.drawer.lifespanDays", "{{count}} Days", { count: model.creativeLifespanDays })}
        />
        <MetricCell
          label={t("drilldown.drawer.duplications", "Duplications")}
          value={t("drilldown.drawer.assetCount", "{{count}} Asset", { count: model.assets })}
        />
        <MetricCell
          label={t("drilldown.drawer.daysPerAsset", "Days / Assets")}
          value={model.daysPerAsset.toFixed(1)}
        />
        <MetricCell
          label={t("drilldown.drawer.rank", "Rank")}
          value={rank !== null ? String(rank) : emptyValue}
          isLoading={isRankLoading}
        />
        <MetricCell label={t("drilldown.drawer.format", "Format")} value={formatLabel} />
      </div>
    </div>
  )
}
